import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import { ReactComponent as EditSVG } from '../../../../assets/images/home/core-benefits-edit.svg';
import { ReactComponent as EyeSVG } from '../../../../assets/images/home/core-benefits-eye.svg';
import { ReactComponent as PaperSVG } from '../../../../assets/images/home/core-benefits-paper.svg';
import { ReactComponent as SearchSVG } from '../../../../assets/images/home/core-benefits-search.svg';
import { ReactComponent as StarSVG } from '../../../../assets/images/home/core-benefits-star.svg';
import { ReactComponent as TabletSVG } from '../../../../assets/images/home/core-benefits-tablet.svg';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PreFooterBlock from '../../components/PreFooterBlock';
import './styles.scss';

const coreBenefitsSteps = [
  {
    iconComponent: <EditSVG />,
    title: 'Custom Logs for Any Task',
  },
  {
    iconComponent: <TabletSVG />,
    title: 'Mobile Shift Assignments',
  },
  {
    iconComponent: <PaperSVG />,
    title: 'Paperless Recordkeeping',
  },
  {
    iconComponent: <EyeSVG />,
    title: 'Real-Time Oversight',
  },
  {
    iconComponent: <SearchSVG />,
    title: 'Instantly Searchable Archives',
  },
  {
    iconComponent: <StarSVG />,
    title: 'Boosted Team Accountability',
  },
];

const OperationsPage = () => {
  const coreBenefitsSwiperRef = useRef();

  const [isOpenScheduleDemoModal, setIsOpenScheduleDemoModal] = useState(false);
  const images = [
    { src: '/images/home/operations-see-in-action-1.jpg' },
    { src: '/images/home/operations-see-in-action-2.jpg' },
    { src: '/images/home/operations-see-in-action-3.jpg' },
    { src: '/images/home/operations-see-in-action-4.jpg' },
  ];
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <main className={cx('home-page operations-page', { isMobile })}>
      <Header setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal} isOpenScheduleDemoModal={isOpenScheduleDemoModal} />
      <div className="home-page-content">
        <div className="streamline">
          <div className="streamline-left">
            <h2 className="streamline-title">
              <span className="blue-part">Streamline Your</span>
              <br /> Day-to-Day Operations
            </h2>
            <p className="streamline-subtitle">
              Digitize shift assignments, maintenance logs, inspection forms, and more—all in one place.
            </p>
            <button className="request-demo-btn" onClick={() => setIsOpenScheduleDemoModal(true)}>
              Request a Demo{' '}
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 9.89771H14.25" stroke="#448AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M9 4.64771L14.25 9.89771L9 15.1477"
                  stroke="#448AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="manual-process">
          <div className="manual-process-left-block">
            <h3 className="say-goodbye-title">Eliminate Operational Stress by Utilizing One Platform for All Your Needs </h3>
          </div>
          <div className="manual-process-right-block">
            <ul className="manual-process-list">
              <li className="manual-process-list-item">No Handwritten logs that get misplaced</li>
              <li className="manual-process-list-item">No Inconsistent shift assignments</li>
              <li className="manual-process-list-item">No Missed or undocumented maintenance checks</li>
              <li className="manual-process-list-item">No Time-consuming recordkeeping</li>
            </ul>
          </div>
        </div>

        <div className="operations-core-capabilities">
          <h3 className="core-capabilities-title">Our Core Capabilities</h3>

          {isMobile ? (
            <div className="core-capabilities-mobile-wrapper">
              <div className="core-capabilities-row core-capabilities-row-1">
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-eye-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Bed Boards Accessible on Phones by Entire Team</h4>
                  </div>
                </div>
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-tool-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Maintenance Requests</h4>
                    <p className="core-capabilities-item-subtitle">Log rounds, safety checks, and repairs with digital forms</p>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-2">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-screenshot-wrapper">
                    <img className="core-capabilities-item-screenshot-img" src="/images/home/cna.jpg" />
                  </div>
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-clock-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">CNA Shift Assignments</h4>
                    <p className="core-capabilities-item-subtitle">Assign residents to staff each shift; accessible via tablet or phone</p>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-3">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-screenshot-wrapper">
                    <img className="core-capabilities-item-screenshot-img" src="/images/home/operations-team-screenshot.png" />
                  </div>
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-file-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Admission Agreements Electronically Signed</h4>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-4">
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-edit-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Custom Logs</h4>
                    <p className="core-capabilities-item-subtitle">Track any process—bed boards, narcotic counts, fridge temps, and more</p>
                  </div>
                </div>
                <div className="core-capabilities-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-search-mobile.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">Instant Retrieval</h4>
                    <p className="core-capabilities-item-subtitle">Look up operational records anytime from any device</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="core-capabilities-grid-container">
              <div className="core-capabilities-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-eye.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Bed Boards Accessible on Phones by Entire Team</h4>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-easy-audit">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-tool.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Maintenance Requests</h4>
                  <p className="core-capabilities-item-subtitle">Log rounds, safety checks, and repairs with digital forms</p>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-tall">
                <div>
                  <img className="core-capabilities-item-screenshot-img" src="/images/home/operations-team-screenshot.png" />
                </div>
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-operations-file.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">
                    Admission
                    <br />
                    Agreements
                    <br />
                    Electronically
                    <br />
                    Signed
                  </h4>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-wide">
                <div className="core-capabilities-item-screenshot-wrapper">
                  <img className="core-capabilities-item-screenshot-img" src="/images/home/cna.jpg" />
                </div>
                <div className="core-capabilities-item-right-block">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-capabilities-clock.svg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <h4 className="core-capabilities-item-title">CNA Shift Assignments</h4>
                    <p className="core-capabilities-item-subtitle">Assign residents to staff each shift; accessible via tablet or phone</p>
                  </div>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-semi-wide">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-edit.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Custom Logs</h4>
                  <p className="core-capabilities-item-subtitle">Track any process—bed boards, narcotic counts, fridge temps, and more</p>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-item-semi-wide">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-search.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Instant Retrieval</h4>
                  <p className="core-capabilities-item-subtitle">Look up operational records anytime from any device</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="operations-see-in-action">
          <h3 className="see-in-action-title">See It In Action</h3>
          <div className="see-in-action-images-wrapper">
            {images.map((image, idx) => (
              <div
                className="see-in-action-image-wrapper"
                key={idx}
                onClick={() => {
                  setIndex(idx);
                  setOpen(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                <img className="see-in-action-img" src={image.src} alt={`See in action ${idx + 1}`} />
                <img className="scale-icon" src="/images/home/scale-icon.svg" />
              </div>
            ))}
          </div>

          <Lightbox
            open={open}
            close={() => setOpen(false)}
            index={index}
            slides={images}
            plugins={[Thumbnails]}
            thumbnails={{
              border: 0,
              vignette: false,
              height: 48,
              width: 48,
              padding: 0,
              gap: 14,
              imageFit: 'cover',
            }}
            styles={{
              container: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
              },
              slide: {
                width: '80vw',
                maxWidth: '80vw',
                maxHeight: '75vh',
                height: '75vh',
                margin: 'auto',
                borderRadius: '10px',
              },
            }}
          />
        </div>

        <div className="compliance-core-benefits">
          <h3 className="compliance-core-benefits-title">Core Benefits</h3>
          <div className="compliance-core-benefits-slider">
            <Swiper
              modules={[Navigation, Pagination]}
              onSwiper={(swiper) => (coreBenefitsSwiperRef.current = swiper)}
              spaceBetween={20}
              loop
              slidesPerView={isMobile ? 1 : 1.5}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2.5 },
                1024: { slidesPerView: 3.2 },
              }}
            >
              {coreBenefitsSteps.map((step, index) => (
                <SwiperSlide key={index}>
                  <div className="compliance-core-benefits-slide">
                    <div style={{ height: '30px' }}></div>
                    <div className="icon-wrapper">{step.iconComponent}</div>
                    <div className="compliance-core-benefits-slide-content">
                      <p className="compliance-core-benefits-title">{step.title}</p>
                      <p className="compliance-core-benefits-subtitle">{step.subtitle}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="custom-nav">
              <button onClick={() => coreBenefitsSwiperRef.current?.slidePrev()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12.7954H5" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 19.7954L5 12.7954L12 5.79541"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button onClick={() => coreBenefitsSwiperRef.current?.slideNext()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12.7954H19" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 5.79541L19 12.7954L12 19.7954"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <PreFooterBlock
          title="Every task, shift, and log — digitally managed."
          subtitle="AllCareForms transforms your operational workflows and keeps your team aligned."
          setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal}
        />
      </div>

      <Footer />
    </main>
  );
};

export default OperationsPage;
